<template>
  <div class="flex justify-between items-start scrollbar-none overflow-x-auto flex-nowrap shrink-0 lg:overflow-visible">
    <div class="flex gap-2 md:flex-wrap md:w-full md:justify-starts md:px-6 lg:px-0">
      <div class="w-2 sm:w-4 md:hidden" />
      <vetster-filter-button
        v-for="(filter, index) in filters"
        :id="filter.id"
        :key="`fixed-filter-${index}`"
        :label="filter.label"
        :icon="filter.icon"
        @click="$emit(filter.event)"
      />

      <vetster-filter-button
        v-for="(concern, index) in concernsVisible"
        :id="`concern-filter-${index}`"
        :key="`concern-filter-${index}`"
        :label="getCxConcernLabel(concern)"
        :icon="getConcernIcon(concern)"
        show-remove
        @click="removePetConcern(concern)"
      />

      <!-- Desktop only "All filters" button -->
      <div class="relative">
        <vetster-filter-button
          id="all-filters-btn"
          :icon="['fal', 'sliders']"
          :label="$t('search.all-filters')"
          class="hidden lg:relative lg:block bg-white lg:border lg:border-ocean-200"
          @click="$emit('toggle-filters')"
        />
        <div
          v-if="concernsCount > 1"
          class="hidden lg:flex items-center justify-center text-xs absolute -right-2 -top-2 size-6 rounded-3xl text-white bg-ocean-950"
        >
          {{ concernsCount - 1 }}
        </div>
      </div>
      <div class="w-2 sm:w-6 md:hidden" />
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendar,
  faLocationDot,
  faSliders,
} from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import size from 'lodash/size';
import take from 'lodash/take';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';

import VetsterFilterButton from '@/brand-components/VetsterFilterButton';
import PetSpecies from '@/enums/PetSpecies';
import HasHealthConcerns from '@/mixins/HasHealthConcerns';
import PetConcernsIcons from '@/mixins/PetConcerns';
import PetIcons from '@/mixins/PetIcons';

library.add({
  faCalendar,
  faLocationDot,
  faSliders,
  faXmark,
});

export default {
  name: 'VetsterSearchButtonFilters',

  components: { VetsterFilterButton },

  mixins: [
    HasHealthConcerns,
    PetConcernsIcons,
    PetIcons,
  ],

  props: {
    speciesTreated: {
      type: String,
      default: null,
    },
  },

  computed: {
    ...mapGetters('pets', ['getPets']),
    ...mapGetters('search', [
      'displayCity',
      'displayDate',
      'displayDateShort',
      'getInputs',
      'hasConcern',
      'isToday',
    ]),
    concernsCount() {
      return size(this.petConcerns);
    },
    concernsVisible() {
      return take(this.petConcerns, this.$screen('desktop') ? 1 : this.concernsCount);
    },
    filters() {
      return [
        {
          id: 'city-filter',
          label: this.displayCity,
          icon: ['fal', 'location-dot'],
          event: 'change-location',
        },
        {
          id: 'date-filter',
          label: this.getDateFilterLabel,
          icon: ['fal', 'calendar'],
          event: 'toggle-filters',
        },
        {
          id: 'pet-filter',
          label: this.getPetName,
          icon: this.speciesTreatedFilterIcon,
          event: 'toggle-filters',
        },
      ];
    },
    getDateFilterLabel() {
      if (!this.getInputs.date_from) {
        return this.$t('search.next-available');
      }

      return DateTime.fromISO(this.getInputs.date_from).toFormat('ccc, LLL d');
    },
    getPet() {
      return this.getPets.find((pet) => pet.uuid === this.getInputs.pet_uuid);
    },
    getPetName() {
      return this.getPet?.name || this.getPetSpeciesText;
    },
    getPetSpeciesText() {
      if (this.speciesTreated) {
        return this.$t(`pets.species.${this.speciesTreated}`);
      }

      return this.$t('search.select-species');
    },
    // The speciesTreated can be a PetSpecies or a SpeciesTreatedType
    isSpecies() {
      return PetSpecies.values().includes(this.speciesTreated);
    },
    petConcerns() {
      return this.getInputs.concerns;
    },
    speciesTreatedFilterIcon() {
      return this.isSpecies ? this.petIcon(this.speciesTreated) : this.speciesTreatedIcon(this.speciesTreated);
    },
  },

  watch: {
    speciesTreated: {
      async handler(value) {
        if (this.hasConcern) {
          await this.$store.dispatch('petConcerns/fetchPetConcerns', {
            pet_species: value,
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    removePetConcern(valueToRemove) {
      const concerns = this.petConcerns.filter((value) => value !== valueToRemove);

      this.$store.commit('search/setTempInputs', { concerns });

      this.$emit('refresh-search');
    },
  },
};
</script>
